import React from "react";
import {
  BarChart, Bar, Cell,
  PieChart, Pie,
  Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import { useIntl } from "react-intl";
import styles from "../styles/AdminStats.module.scss";
import { UserStats } from "../types/adminStats.type";


interface UsersSectionProps {
  data: UserStats | null;
}

const COLORS = {
  blue:   "#4f6ef7",
  green:  "#3ecf8e",
  purple: "#7c4ff7",
  red:    "#f74f6a",
  orange: "#f7934f",
};

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      {label && <div className={styles.tooltipLabel}>{label}</div>}
      {payload.map((p: any) => (
        <div key={p.name} className={styles.tooltipRow}>
          <span className={styles.tooltipDot} style={{ background: p.color || p.fill }} />
          <span className={styles.tooltipName}>{p.name}:</span>
          <span className={styles.tooltipValue}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function UsersSection({ data }: UsersSectionProps): React.ReactElement {
  const intl = useIntl();
  const userPieData = data
    ? [
        { name: intl.formatMessage({ id: "admin.stats.users.creators", defaultMessage: "Creators" }),  value: data.creators,  color: COLORS.purple },
        { name: intl.formatMessage({ id: "admin.stats.users.customers", defaultMessage: "Customers" }), value: data.customers, color: COLORS.blue   },
        { name: intl.formatMessage({ id: "admin.stats.users.banned", defaultMessage: "Banned" }),    value: data.banned,    color: COLORS.red    },
      ]
    : [];

  const userBarData = data
    ? [
        { name: "Creators",  count: data.creators,  color: COLORS.purple },
        { name: "Customers", count: data.customers, color: COLORS.blue   },
        { name: "Banned",    count: data.banned,    color: COLORS.red    },
      ]
    : [];

  return (
    <>
      {/* ── Stat cards ── */}
      <div className={`ui four stackable cards ${styles.cardRow}`}>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.users.totalUsers", defaultMessage: "Total Users" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.blue }}>
              {data?.totalUsers ?? "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.users.creators", defaultMessage: "Creators" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.purple }}>
              {data?.creators ?? "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.users.customers", defaultMessage: "Customers" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.green }}>
              {data?.customers ?? "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.users.banned", defaultMessage: "Banned" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.red }}>
              {data?.banned ?? "—"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Charts ── */}
      <div className={styles.chartsRow}>

        {/* Donut — user type breakdown */}
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.users.breakdown", defaultMessage: "User Breakdown" })}</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={userPieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
              >
                {userPieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(val) => (
                  <span className={styles.legendLabel}>{val}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar — user type counts */}
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.users.typeCounts", defaultMessage: "User Type Counts" })}</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={userBarData}
              barSize={40}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ fill: "rgba(79,110,247,0.06)" }}
              />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {userBarData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className={styles.barLabels}>
            {userBarData.map((d) => (
              <div key={d.name} className={styles.barLabel}>
                <span className={styles.barDot} style={{ background: d.color }} />
                {d.name}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Top Users Table ── */}
      {data?.topUsers?.length ? (
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.users.topCustomers", defaultMessage: "Top Customers" })}</div>
          <table className="ui very basic compact table">
            <thead>
              <tr>
                <th>#</th>
                <th>{intl.formatMessage({ id: "admin.stats.users.name", defaultMessage: "Name" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.users.totalSpent", defaultMessage: "Total Spent" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.users.orders", defaultMessage: "Orders" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.users.avgOrder", defaultMessage: "Avg. Order" })}</th>
              </tr>
            </thead>
            <tbody>
              {data.topUsers.map((user, i) => {
                const avg = user.totalOrders > 0
                  ? (user.totalSpent / user.totalOrders).toFixed(2)
                  : "0.00";
                return (
                  <tr key={user._id}>
                    <td style={{ color: "#888", fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{user.name}</td>
                    <td style={{ color: COLORS.green, fontWeight: 600 }}>
                      ${user.totalSpent.toLocaleString()}
                    </td>
                    <td style={{ color: COLORS.blue }}>{user.totalOrders}</td>
                    <td style={{ color: COLORS.orange }}>${avg}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}